/**
 * Filters Module
 *
 * Reusable filter handling for index pages.
 * Auto-initializes on elements with data-controller="filters".
 *
 * Usage in Twig:
 * <div data-controller="filters" data-base-url="/products">
 *     <select data-filter="category" data-filter-param="category">
 *         <option value="">All Categories</option>
 *     </select>
 *     <select data-filter="status" data-filter-param="status">
 *         <option value="">All Status</option>
 *     </select>
 *     <button data-filter-clear>Clear Filters</button>
 * </div>
 */

import { $, $$, on, ready, getData } from '../utils/dom.js';

/**
 * Initialize filters on a container
 * @param {Element} container - The container element
 */
function initFilters(container) {
    const config = getData(container);
    const baseUrl = config.baseUrl || window.location.pathname;

    /**
     * Build URL with current filter values
     * @returns {string} URL with query params
     */
    function buildFilterUrl() {
        const params = new URLSearchParams();

        // Get all filter elements and their values
        $$('[data-filter]', container).forEach(el => {
            const paramName = el.dataset.filterParam || el.name || el.dataset.filter;
            const value = el.value;

            if (value) {
                params.append(paramName, value);
            }
        });

        // Preserve other query params not managed by filters
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.forEach((value, key) => {
            // Only preserve params not in our filters
            if (!$(`[data-filter-param="${key}"], [data-filter="${key}"]`, container)) {
                params.append(key, value);
            }
        });

        const queryString = params.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    /**
     * Apply filters - navigate to new URL
     */
    function applyFilters() {
        window.location.href = buildFilterUrl();
    }

    /**
     * Clear all filters
     */
    function clearFilters() {
        window.location.href = baseUrl;
    }

    // Event listeners for filter changes
    on(container, 'change', '[data-filter]', () => {
        applyFilters();
    });

    // Clear filters button
    const clearBtn = $('[data-filter-clear]', container);
    if (clearBtn) {
        clearBtn.addEventListener('click', (e) => {
            e.preventDefault();
            clearFilters();
        });
    }
}

/**
 * Auto-initialize on page load
 */
ready(() => {
    $$('[data-controller="filters"]').forEach(initFilters);
});

// Export global functions for inline onclick handlers (legacy support)
window.applyFilters = function(selectElement) {
    // Find the closest filters container
    const container = selectElement.closest('[data-controller="filters"]');
    if (container) {
        const baseUrl = container.dataset.baseUrl || window.location.pathname;
        const params = new URLSearchParams();

        container.querySelectorAll('[data-filter]').forEach(el => {
            const paramName = el.dataset.filterParam || el.name || el.dataset.filter;
            if (el.value) {
                params.append(paramName, el.value);
            }
        });

        window.location.href = params.toString() ? `${baseUrl}?${params}` : baseUrl;
    }
};

window.applyStatusFilter = function(selectElement) {
    window.applyFilters(selectElement);
};

window.clearFilters = function() {
    // Find the closest filters container or use pathname
    const container = document.querySelector('[data-controller="filters"]');
    const baseUrl = container ? container.dataset.baseUrl : window.location.pathname;
    window.location.href = baseUrl;
};

/**
 * Manual initialization function
 * @param {Element|string} target - Element or selector
 */
export function init(target) {
    const element = typeof target === 'string' ? $(target) : target;
    if (element) {
        initFilters(element);
    }
}

export default {
    init
};
